import { useMemo } from "react";
import { Box, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import PatientDatas from "src/modules/medcard/patient-datas";
import { PatientChartSummaryData } from "src/api/nurse/nurseServiceDtos";

type FormInfosProps = {
  patientId?: number;
  summary: PatientChartSummaryData;
  loading?: boolean;
};

const formatMetric = (value: number | null | undefined, options?: { signed?: boolean; decimals?: number }) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "-";
  }

  const decimals = options?.decimals ?? 0;
  const fixedValue = decimals > 0 ? value.toFixed(decimals) : String(Math.round(value));

  if (options?.signed && value > 0) {
    return `+ ${fixedValue}`;
  }

  if (options?.signed && value === 0) {
    return "0";
  }

  return fixedValue;
};

export default function FormInfos({ patientId, summary, loading = false }: Readonly<FormInfosProps>) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const scoreCards = useMemo(
    () => [
      {
        title: "Sıvı Balansı",
        value: loading ? "..." : formatMetric(summary.fluidbalance, { signed: true }),
        path: patientId ? `/medcard/fluid-balance/${patientId}?orId=2553` : undefined,
      },
      {
        title: "Ağrı Skalası",
        value: loading ? "..." : formatMetric(summary.painscale),
        path: patientId ? `/medcard/pain-assessment-scale/${patientId}?orId=2555` : undefined,
      },
      {
        title: "Kan Şekeri Takibi",
        value: loading ? "..." : formatMetric(summary.bloodsugartrackingvalue),
        path: patientId ? `/medcard/blood-sugar-trace/${patientId}?orId=2606` : undefined,
      },
      {
        title: "NRS Değerlendirme",
        value: loading ? "..." : formatMetric(summary.nrsassessment, { decimals: 2 }),
        path: patientId ? `/medcard/nutrition-risk-score/${patientId}?orId=2561` : undefined,
      },
    ],
    [loading, patientId, summary]
  );

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        justifyContent: "flex-start",
        alignItems: "stretch",
        gap: "16px",
      }}
    >
      <Box
        sx={{
          flex: { xs: "unset", md: 1 },
          minWidth: 0,
          height: { xs: "auto", md: "360px" },
          background: "#fff",
          borderRadius: "8px",
          overflow: "hidden",
          boxShadow:
            "0px 10px 24px -8px rgba(42, 51, 70, 0.03), 0px 0px 0px 1px rgba(0, 0, 0, 0.07)",
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "46px",
            px: "16px",
            py: "8px",
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Box
            sx={{
              width: "26px",
              height: "26px",
              borderRadius: "100px",
              background: "#F5F5F5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              component="img"
              src="/assets/icons/generals/users.svg"
              alt=""
              sx={{ width: 14, height: 14, objectFit: "contain" }}
            />
          </Box>
          <Typography
            sx={{
              flex: 1,
              color: "#000",
              fontSize: "16px",
              fontFamily: "Poppins",
              fontWeight: 700,
              lineHeight: "24px",
            }}
          >
            Skor Formları
          </Typography>
        </Box>

        <Box
          sx={{
            width: "100%",
            height: { xs: "auto", md: "314px" },
            px: "16px",
            pt: "12px",
            pb: "16px",
            background: "#fff",
            boxShadow: "0px 0px 0px 0.5px rgba(0, 0, 0, 0.07)",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          {isMobile ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: "12px",
              }}
            >
              {scoreCards.map((item) => (
                <ScoreItem
                  key={item.title}
                  title={item.title}
                  value={item.value}
                  onClick={item.path ? () => navigate(item.path!) : undefined}
                  mobile
                />
              ))}
            </Box>
          ) : (
            <>
              <Stack direction="row" gap="12px">
                {scoreCards.slice(0, 2).map((item) => (
                  <ScoreItem
                    key={item.title}
                    title={item.title}
                    value={item.value}
                    onClick={item.path ? () => navigate(item.path!) : undefined}
                  />
                ))}
              </Stack>
              <Stack direction="row" gap="12px">
                {scoreCards.slice(2).map((item) => (
                  <ScoreItem
                    key={item.title}
                    title={item.title}
                    value={item.value}
                    onClick={item.path ? () => navigate(item.path!) : undefined}
                  />
                ))}
              </Stack>
            </>
          )}
        </Box>
      </Box>

      <Box sx={{ flex: { xs: "unset", md: 2 }, minWidth: 0, height: { xs: "auto", md: "360px" }, overflow: "hidden" }}>
        <PatientDatas
          patientId={patientId}
          noMargin
          onlyVitalsSection
          showBloodPressureChart
          showDailyPulseChart
        />
      </Box>
    </Box>
  );
}

function ScoreItem({
  title,
  value,
  onClick,
  mobile = false,
}: {
  title: string;
  value: string;
  onClick?: () => void;
  mobile?: boolean;
}) {
  return (
    <Box
      sx={{
        width: mobile ? "100%" : "230px",
        minWidth: 0,
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "center",
        gap: mobile ? "12px" : "16px",
        textAlign: "left",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "4px", minWidth: 0 }}>
        <Typography
          sx={{
            color: "#080808",
            fontSize: "14px",
            fontFamily: "Poppins",
            fontWeight: 700,
            lineHeight: "21px",
            textAlign: "left",
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            color: "#2979C3",
            fontSize: "24px",
            fontFamily: "Poppins",
            fontWeight: 400,
            lineHeight: "36px",
            textAlign: "left",
          }}
        >
          {value}
        </Typography>
      </Box>

      <Box
        onClick={onClick}
        sx={{
          px: "12px",
          py: "8px",
          borderRadius: "5px",
          outline: "1px solid #2979C3",
          outlineOffset: "-1px",
          width: "fit-content",
          cursor: onClick ? "pointer" : "default",
          transition: "all 0.2s ease",
          "&:hover": onClick
            ? {
                backgroundColor: "rgba(41, 121, 195, 0.06)",
              }
            : undefined,
        }}
      >
        <Typography
          sx={{
            color: "#2979C3",
            fontSize: "12px",
            fontFamily: "Poppins",
            fontWeight: 400,
            textAlign: "center",
          }}
        >
          Tüm Sonuçlar
        </Typography>
      </Box>
    </Box>
  );
}
